import * as React from 'react'

import { cn } from '@/registry/reapit/lib/utils'

/**
 * DS 3XL — 36px Semi Bold (−0.01em tracking).
 * Before: text-4xl font-extrabold lg:text-5xl.
 * After: text-3xl font-semibold tracking-tight (no responsive hop — DS defines no >3XL style).
 */
function TypographyH1({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h1
      data-slot="typography-h1"
      className={cn('scroll-m-20 text-3xl font-semibold tracking-tight', className)}
      {...props}
    />
  )
}

/**
 * DS 2XL — 28px Semi Bold.
 * Before: text-3xl font-semibold (30px).
 * After: text-2xl font-semibold (28px after Phase 1 override).
 */
function TypographyH2({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      data-slot="typography-h2"
      className={cn('scroll-m-20 border-b pb-2 text-2xl font-semibold tracking-tight first:mt-0', className)}
      {...props}
    />
  )
}

/**
 * DS XL — 24px Semi Bold.
 * No change: text-xl font-semibold aligns to DS XL after Phase 1 override (was already 24px).
 */
function TypographyH3({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      data-slot="typography-h3"
      className={cn('scroll-m-20 text-xl font-semibold tracking-tight', className)}
      {...props}
    />
  )
}

/**
 * DS L — 20px Semi Bold.
 * Before: text-xl font-semibold (20px).
 * After: text-lg font-semibold (20px after Phase 1 override — lg now maps to DS L).
 */
function TypographyH4({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h4
      data-slot="typography-h4"
      className={cn('scroll-m-20 text-lg font-semibold tracking-tight', className)}
      {...props}
    />
  )
}

function TypographyP({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p data-slot="typography-p" className={cn('leading-7 [&:not(:first-child)]:mt-6', className)} {...props} />
}

function TypographyBlockquote({ className, ...props }: React.HTMLAttributes<HTMLQuoteElement>) {
  return (
    <blockquote data-slot="typography-blockquote" className={cn('mt-6 border-l-2 pl-6 italic', className)} {...props} />
  )
}

function TypographyCode({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
  return (
    <code
      data-slot="typography-code"
      className={cn('bg-muted relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold', className)}
      {...props}
    />
  )
}

/**
 * DS L body text with secondary colour.
 * Before: text-xl text-muted-foreground (20px).
 * After: text-lg text-muted-foreground (20px after Phase 1 override — lg now maps to DS L).
 */
function TypographyLead({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p data-slot="typography-lead" className={cn('text-muted-foreground text-lg', className)} {...props} />
}

/**
 * @deprecated There is no DS-equivalent 18px style. Use `<TextHeading>` (DS XL, 24px)
 * or `<Text size="lg" weight="medium">` (DS L, 20px) instead.
 * Retained as a backwards-compatible alias — visual output unchanged from pre-Phase 2.
 */
function TypographyLarge({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="typography-large" className={cn('text-lg font-semibold', className)} {...props} />
}

/** DS S Medium (14px 500) — confirmed ✓. */
function TypographySmall({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
  return <small data-slot="typography-small" className={cn('text-sm font-medium leading-none', className)} {...props} />
}

/** DS S secondary (14px Regular, muted colour) — confirmed ✓. */
function TypographyMuted({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p data-slot="typography-muted" className={cn('text-muted-foreground text-sm', className)} {...props} />
}

export {
  TypographyH1,
  TypographyH2,
  TypographyH3,
  TypographyH4,
  TypographyP,
  TypographyBlockquote,
  TypographyCode,
  TypographyLead,
  TypographyLarge,
  TypographySmall,
  TypographyMuted,
}
